// src/hooks/useLocalDraft.js
//
// Auto-save in-progress edits to localStorage so they survive tab close, refresh,
// and accidental nav. Pairs with the existing isDirty / dirty-guard pattern.
//
// Usage:
//   useLocalDraft({ key, isDirty, snapshot });
//   const draft = readLocalDraft(key);
//   clearLocalDraft(key);

import { useEffect, useRef } from 'react';

/**
 * Persist `snapshot` to localStorage under `key` whenever it changes (while dirty),
 * debounced to avoid thrashing on every keystroke. Also writes on `beforeunload`
 * as a final safety net before the debounce fires.
 */
export function useLocalDraft({ key, isDirty, snapshot, debounceMs = 500, enabled = true }) {
  const timeoutRef = useRef(null);

  // Debounced save while dirty
  useEffect(() => {
    if (!enabled || !isDirty || !key) return undefined;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      try {
        localStorage.setItem(
          key,
          JSON.stringify({ ts: Date.now(), data: snapshot })
        );
      } catch (err) {
        console.warn('Local draft save failed:', err);
      }
    }, debounceMs);
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, [key, isDirty, snapshot, debounceMs, enabled]);

  // Capture-on-close — in case the user navigates / closes before debounce fires
  useEffect(() => {
    const handler = () => {
      if (!enabled || !isDirty || !key) return;
      try {
        localStorage.setItem(
          key,
          JSON.stringify({ ts: Date.now(), data: snapshot })
        );
      } catch {
        // ignore — beforeunload, nothing we can do about errors
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [key, isDirty, snapshot, enabled]);
}

export function readLocalDraft(key) {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearLocalDraft(key) {
  if (!key) return;
  try { localStorage.removeItem(key); } catch {}
}
